/**
 * Hook that provides a user lookup map from organization memberships.
 * Used to resolve Clerk user IDs to user details (name, avatar).
 */

import { useOrganization } from '@clerk/clerk-react'
import { useMemo } from 'react'

export interface UserLookupEntry {
  id: string
  name: string
  avatarUrl?: string
}

export type UserLookupMap = Map<string, UserLookupEntry>

interface UseOrgMembersLookupReturn {
  userLookup: UserLookupMap
  isLoading: boolean
}

const buildUserName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  identifier: string | null | undefined
): string => {
  if (firstName && lastName) return `${firstName} ${lastName}`
  if (firstName) return firstName
  if (lastName) return lastName
  if (identifier) return identifier
  return 'Unknown User'
}

export const useOrgMembersLookup = (): UseOrgMembersLookupReturn => {
  const { memberships, isLoaded } = useOrganization({ memberships: { pageSize: 100 } })

  const userLookup = useMemo<UserLookupMap>(() => {
    const map = new Map<string, UserLookupEntry>()

    if (!memberships?.data) return map

    for (const membership of memberships.data) {
      const userData = membership.publicUserData
      if (!userData?.userId) continue

      map.set(userData.userId, {
        id: userData.userId,
        name: buildUserName(userData.firstName, userData.lastName, userData.identifier),
        avatarUrl: userData.imageUrl ?? undefined,
      })
    }

    return map
  }, [memberships?.data])

  return {
    userLookup,
    isLoading: !isLoaded,
  }
}
